import { useState, useEffect, useRef, type ReactNode } from 'react';
import {
  ArrowDownTrayIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClockIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { Input } from '@components/ui';
import {
  CTA,
  DASHED_RULE_STYLE,
  FIELD_HINT,
  FORM_LABEL,
  FORM_SKIN,
  INSET_PANEL,
  METRIC_LABEL,
  SECTION_NUM,
} from '@components/ui/designTokens';
import { cn } from '@lib/utils/cn';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { reportService } from '../services/reportService';
import type { AludraBranchOffice } from '../types/report.types';
import { showSuccess, showError, showWarning, showBalanceSheetWaitNotice, showReportRetryOrEmailPrompt } from '@/utils/sweetAlert';
import { useAuthStore } from '@features/auth/store/authStore';
import { getHomeRouteForRole } from '@lib/utils/roleNavigation';
import {
  BALANCE_SHEET_NOTICE_MS,
  BALANCE_SHEET_CUTOFF_MS,
  classifyReportResponse,
} from '../utils/balanceSheetEmailFallback';

// Helper function to get date in YYYY-MM-DD format
const getFormattedDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get today's date
const today = new Date();
const todayFormatted = getFormattedDate(today);

// Get date one month ago
const oneMonthAgo = new Date();
oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
const oneMonthAgoFormatted = getFormattedDate(oneMonthAgo);

/**
 * Tarjeta del catalogo de reportes (`.rcat` de Reportes.dc.html): barra de
 * acento arriba segun el formato de salida, baldosa de icono, kicker numerado,
 * y el bloque de filtros hundido; el boton de generar queda anclado abajo para
 * que las tres tarjetas terminen a la misma altura.
 *
 * Solo presentacion: no decide que reportes existen ni que filtros llevan.
 */
function ReportCard({
  index,
  format,
  icon,
  title,
  description,
  featured = false,
  children,
  footnote,
  action,
}: {
  index: string;
  format: 'xlsx' | 'pdf';
  icon: ReactNode;
  title: string;
  description: string;
  /** Realza la tarjeta (el diseño destaca el Reporte Z). */
  featured?: boolean;
  children: ReactNode;
  footnote?: ReactNode;
  action: ReactNode;
}) {
  const isPdf = format === 'pdf';

  return (
    <div
      className={cn(
        'relative flex flex-col overflow-hidden rounded-[16px] border bg-white p-6 transition-all duration-150 hover:-translate-y-0.5 dark:bg-secondary-800',
        featured
          ? 'border-[#6b239e]/25 shadow-[0_1px_2px_rgba(10,34,51,.03),0_20px_40px_-20px_rgba(107,35,158,.24)]'
          : 'border-[#ececec] shadow-[0_1px_2px_rgba(10,34,51,.03),0_12px_32px_-18px_rgba(10,34,51,.14)] hover:border-secondary-300 dark:border-secondary-700'
      )}
    >
      {/* Barra de acento del formato */}
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-x-0 top-0 h-1',
          isPdf
            ? 'bg-gradient-to-r from-[#6b239e] to-accent-500'
            : 'bg-gradient-to-r from-teal-500 to-[#0d9488]'
        )}
      />

      <div className="flex items-center justify-between gap-3">
        <span
          className={cn(
            'inline-flex h-11 w-11 items-center justify-center rounded-[11px] [&_svg]:h-5 [&_svg]:w-5',
            isPdf
              ? 'bg-gradient-to-br from-[#6b239e]/[.14] to-accent-500/10 text-[#6b239e]'
              : 'bg-teal-500/10 text-[#0d9488]'
          )}
        >
          {icon}
        </span>
        <div className="flex items-center gap-1.5">
          {featured && (
            <span className="inline-flex items-center rounded-full bg-gradient-to-br from-teal-500/[.14] to-[#6b239e]/[.14] px-2 py-0.5 font-mono text-[11px] font-bold tracking-[0.14em] text-ink-850 dark:text-white">
              POPULAR
            </span>
          )}
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[11px] font-bold tracking-[0.14em]',
              isPdf ? 'bg-[#6b239e]/10 text-[#6b239e]' : 'bg-[#0d9488]/10 text-[#0d9488]'
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'h-[5px] w-[5px] rounded-full',
                isPdf ? 'bg-[#6b239e]' : 'bg-teal-500'
              )}
            />
            {isPdf ? 'PDF' : 'XLSX'}
          </span>
        </div>
      </div>

      <div className="mt-4">
        <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-secondary-400">
          Reporte · {index}
        </div>
        <h3 className="mt-1.5 text-[17px] font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
          {title}
        </h3>
        <p className="mt-2 text-[13px] leading-[1.55] text-secondary-500 dark:text-secondary-400">
          {description}
        </p>
      </div>

      <div className={cn(INSET_PANEL, FORM_SKIN, 'mt-[18px] p-3.5')}>{children}</div>

      {footnote && <div className="mt-3 flex-1">{footnote}</div>}
      {!footnote && <div className="mt-3 flex-1" />}

      <div className="mt-3.5">{action}</div>
    </div>
  );
}

/**
 * Filtro opcional por sucursales. Conserva los tres estados que ya tenia la
 * pantalla (cargando / sin sucursales / lista de checkboxes con el contador de
 * seleccionadas); el diseño solo cambia como se ven.
 */
function BranchFilter({
  branchOffices,
  isLoading,
  selected,
  onToggle,
  testIdBase,
}: {
  branchOffices: AludraBranchOffice[];
  isLoading: boolean;
  selected: string[];
  onToggle: (code: string, checked: boolean) => void;
  /**
   * Prefijo del `data-testid` de cada checkbox: se emite
   * `${testIdBase}-${branch.Code}`. El diseño unifico el selector de sucursales
   * de los reportes Z y de Balance en este componente, pero la automatizacion de
   * QA los distingue, asi que cada uso pasa su propio prefijo.
   */
  testIdBase?: string;
}) {
  const empty = branchOffices.length === 0;

  return (
    <div
      className={cn(
        'rounded-[10px] border border-dashed border-[#e2e8f0] bg-secondary-400/[.08] p-3 dark:border-secondary-600',
        empty && 'flex items-center gap-2'
      )}
    >
      <div className="flex items-center gap-2 text-[12px] text-secondary-400">
        <BuildingOffice2Icon className="h-3 w-3 shrink-0" />
        <span>
          <span className="font-bold">Filtro por sucursales</span> (opcional)
          {isLoading && ' — Cargando sucursales...'}
          {!isLoading && empty && ' — No hay sucursales disponibles'}
        </span>
      </div>

      {!isLoading && !empty && (
        <>
          <div className="mt-2 max-h-40 space-y-0.5 overflow-y-auto">
            {branchOffices.map((branch) => (
              <label
                key={branch.BranchOfficeId}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-[13px] text-ink-850 transition-colors hover:bg-white dark:text-white dark:hover:bg-secondary-700"
              >
                <input
                  type="checkbox"
                  value={branch.Code}
                  checked={selected.includes(branch.Code)}
                  onChange={(e) => onToggle(branch.Code, e.target.checked)}
                  data-testid={testIdBase ? `${testIdBase}-${branch.Code}` : undefined}
                  className="h-4 w-4 rounded-[5px] border-secondary-300 text-[#0d9488] focus:ring-teal-500/40"
                />
                <span className="font-mono text-[12px] font-bold text-secondary-500">
                  {branch.Code}
                </span>
                <span className="truncate">{branch.Description}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <p className={cn(FIELD_HINT, 'mt-1.5')}>
              {selected.length} sucursal{selected.length !== 1 ? 'es' : ''} seleccionada
              {selected.length !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Rotulo de campo con icono de calendario, como en el diseño. */
function DateLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center gap-2">
      <CalendarDaysIcon className="h-3 w-3 text-secondary-400" />
      <label className={FORM_LABEL}>{children}</label>
    </div>
  );
}

export function ReportsPage() {
  const { selectedCompany, companies } = useCompanies({ navigateOnSelect: false });
  const { user } = useAuthStore();
  const homeRoute = getHomeRouteForRole(user?.role);

  console.log('[ReportsPage] selectedCompany:', selectedCompany);
  console.log('[ReportsPage] companies:', companies);

  // Estados para Documentos emitidos - with default date (today)
  const [invoiceDate, setInvoiceDate] = useState(todayFormatted);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  // Estados para Reporte Z - with default dates (one month ago to today)
  const [zStartDate, setZStartDate] = useState(oneMonthAgoFormatted);
  const [zEndDate, setZEndDate] = useState(todayFormatted);
  const [zStoreCodes, setZStoreCodes] = useState<string[]>([]);
  const [isGeneratingZ, setIsGeneratingZ] = useState(false);

  // Estados para Balance General - with default dates (one month ago to today)
  const [balanceInitDate, setBalanceInitDate] = useState(oneMonthAgoFormatted);
  const [balanceEndDate, setBalanceEndDate] = useState(todayFormatted);
  const [balanceStoreCodes, setBalanceStoreCodes] = useState<string[]>([]);
  const [isGeneratingBalance, setIsGeneratingBalance] = useState(false);
  const [isSendingBalanceByEmail, setIsSendingBalanceByEmail] = useState(false);
  // Refs (no state) para el esquema de espera escalonado: se leen dentro de
  // callbacks async/timers donde un closure de useState quedaria obsoleto.
  const balanceEmailFlowChosenRef = useRef(false);
  const balanceAbortControllerRef = useRef<AbortController | null>(null);
  const balanceNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const balanceCutoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Estados para sucursales
  const [branchOffices, setBranchOffices] = useState<AludraBranchOffice[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  // Cargar sucursales al montar el componente
  useEffect(() => {
    const loadBranchOffices = async () => {
      const companyId = selectedCompany?.id;

      if (!companyId) {
        console.log('No company selected, skipping branch offices load');
        return;
      }

      console.log('Loading branch offices for company:', companyId);

      try {
        setIsLoadingBranches(true);
        const response = await reportService.searchBranchOffices(companyId);

        console.log('Branch offices response:', response);

        if (response.Status?.Code === 200 && response.Data && Array.isArray(response.Data)) {
          // Filter only active branch offices
          const activeBranches = response.Data.filter(b => b.IsActive);
          console.log('Active branches found:', activeBranches.length);
          setBranchOffices(activeBranches);
        } else {
          console.warn('Invalid response structure:', response);
        }
      } catch (error) {
        console.error('Error loading branch offices:', error);
      } finally {
        setIsLoadingBranches(false);
      }
    };

    loadBranchOffices();
  }, [selectedCompany?.id]);

  const handleGenerateIssuedDocuments = async () => {
    const companyId = selectedCompany?.id;
    if (!companyId) {
      showWarning('Atención', 'No hay compañía seleccionada');
      return;
    }

    try {
      setIsGeneratingExcel(true);

      const request = {
        FileName: 'Reporte_facturas',
        ReportName: 'DailyElectronicInvoice',
        Filters: {
          CompanyId: companyId,
          ReportDate: invoiceDate,
        },
      };

      console.log('Generating issued documents report:', request);

      const blob = await reportService.generateExcelReport(request);
      reportService.downloadExcelReport(blob, request.FileName);

      showSuccess('¡Éxito!', 'Reporte generado exitosamente');
    } catch (error) {
      console.error('Error generating report:', error);
      showError('Error', 'No se pudo generar el reporte');
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleGenerateZReport = async () => {
    const companyId = selectedCompany?.id;
    const companyCode = selectedCompany?.code;

    if (!companyId) {
      showWarning('Atención', 'No hay compañía seleccionada');
      return;
    }

    if (!companyCode) {
      showWarning('Atención', 'La compañía no tiene código configurado');
      return;
    }

    if (!zStartDate || !zEndDate) {
      showWarning('Atención', 'Por favor seleccione las fechas de inicio y fin');
      return;
    }

    try {
      setIsGeneratingZ(true);

      const filters: any = {
        CompanyId: companyId,
        StartDate: `${zStartDate} 00:00:00`,
        EndDate: `${zEndDate} 23:59:59`,
      };

      // Solo agregar StoreCode si hay códigos seleccionados
      if (zStoreCodes.length > 0) {
        filters.StoreCode = zStoreCodes[0];
      }

      const request = {
        RenderType: 1, // 1 = PDF
        FileName: 'Reporte_Z',
        ReportName: 'FEZReport',
        CompanyCode: companyCode,
        Filters: filters,
      };

      console.log('Generating Z report:', request);

      const blob = await reportService.generateZReport(request);
      reportService.downloadPdfReport(blob, request.FileName);

      showSuccess('¡Éxito!', 'Reporte Z generado exitosamente');
    } catch (error) {
      console.error('Error generating Z report:', error);
      showError('Error', 'No se pudo generar el Reporte Z');
    } finally {
      setIsGeneratingZ(false);
    }
  };

  const clearBalanceTimers = () => {
    if (balanceNoticeTimerRef.current) {
      clearTimeout(balanceNoticeTimerRef.current);
      balanceNoticeTimerRef.current = null;
    }
    if (balanceCutoffTimerRef.current) {
      clearTimeout(balanceCutoffTimerRef.current);
      balanceCutoffTimerRef.current = null;
    }
  };

  const buildBalanceSheetRequest = () => {
    const companyId = selectedCompany?.id;
    const filters: { CompanyId?: string; StartDate: string; EndDate: string; StoreCode?: string } = {
      CompanyId: companyId,
      StartDate: `${balanceInitDate} 00:00:00`,
      EndDate: `${balanceEndDate} 23:59:59`,
    };
    if (balanceStoreCodes.length > 0) {
      filters.StoreCode = balanceStoreCodes[0];
    }
    return { FileName: 'Reporte_Balance_General', ReportName: 'FEInvoiceDetails', Filters: filters };
  };

  const extractErrorMessage = (error: unknown, fallback: string): string => {
    const responseMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    return responseMessage || fallback;
  };

  /**
   * Envia el reporte por correo. Compartido por el aviso de 30s, el corte de
   * 90s, y el boton directo "Generar y enviar por correo" -- en todos los
   * casos marca emailFlowChosenRef=true PRIMERO, para que si la descarga
   * sincrona original responde despues, esa respuesta tardia se ignore
   * (ver shouldIgnoreLateSyncResponse).
   */
  const handleSendBalanceSheetByEmail = async () => {
    if (balanceEmailFlowChosenRef.current) return; // ya en curso/decidido, evita doble envio
    balanceEmailFlowChosenRef.current = true;
    clearBalanceTimers();
    balanceAbortControllerRef.current?.abort();

    const request = buildBalanceSheetRequest();
    try {
      setIsGeneratingBalance(false);
      setIsSendingBalanceByEmail(true);
      await reportService.sendBalanceSheetByEmail(request);
      showSuccess(
        '¡Enviado!',
        'El reporte se esta generando. Te llegara por correo cuando este listo (puede demorar entre 5 minutos y 1 hora).'
      );
    } catch (error) {
      console.error('Error sending balance sheet by email:', error);
      showError('Error', extractErrorMessage(error, 'No se pudo iniciar el envio del reporte por correo. Intenta nuevamente.'));
    } finally {
      setIsSendingBalanceByEmail(false);
    }
  };

  const handleGenerateBalanceSheet = async () => {
    const companyId = selectedCompany?.id;
    if (!companyId) {
      showWarning('Atención', 'No hay compañía seleccionada');
      return;
    }

    if (!balanceInitDate || !balanceEndDate) {
      showWarning('Atención', 'Por favor seleccione las fechas de inicio y fin');
      return;
    }

    balanceEmailFlowChosenRef.current = false;
    const abortController = new AbortController();
    balanceAbortControllerRef.current = abortController;

    // A los 30s: OFRECE la alternativa de correo, sin cancelar nada.
    balanceNoticeTimerRef.current = setTimeout(async () => {
      if (balanceEmailFlowChosenRef.current) return;
      const result = await showBalanceSheetWaitNotice();
      if (result.isConfirmed && !balanceEmailFlowChosenRef.current) {
        handleSendBalanceSheetByEmail();
      }
      // "Seguir esperando" (cancel/dismiss): no hace nada, la descarga sigue.
    }, BALANCE_SHEET_NOTICE_MS);

    // A los 90s totales: CORTA la espera y fuerza unicamente correo.
    balanceCutoffTimerRef.current = setTimeout(() => {
      if (balanceEmailFlowChosenRef.current) return;
      handleSendBalanceSheetByEmail();
    }, BALANCE_SHEET_CUTOFF_MS);

    try {
      setIsGeneratingBalance(true);

      const request = buildBalanceSheetRequest();
      console.log('Generating balance sheet report:', request);

      const blob = await reportService.generateExcelReport(request, { signal: abortController.signal });

      // Condicion de carrera: si ya se opto por correo (aviso o corte)
      // mientras esta respuesta viajaba, se ignora -- no mostrar 2 resultados.
      if (balanceEmailFlowChosenRef.current) return;
      clearBalanceTimers();

      const outcome = classifyReportResponse({
        status: 200,
        contentType: blob.type || null,
        hasFileName: blob.size > 0,
      });

      if (outcome === 'success') {
        reportService.downloadExcelReport(blob, request.FileName);
        showSuccess('¡Éxito!', 'Reporte de Balance General generado exitosamente');
      } else {
        // json-error o invalid: mismo modal de reintentar/correo/cancelar del legado.
        const promptResult = await showReportRetryOrEmailPrompt(
          'No fue posible descargar el reporte en este momento. Puede reintentarlo, enviarlo por correo o cancelar.'
        );
        if (promptResult.isConfirmed) {
          handleGenerateBalanceSheet();
        } else if (promptResult.isDenied) {
          handleSendBalanceSheetByEmail();
        }
      }
    } catch (error) {
      clearBalanceTimers();
      // Abort disparado por el corte de 90s o por handleSendBalanceSheetByEmail:
      // ya se esta manejando el flujo de correo, no mostrar error aparte.
      const errorCode = (error as { code?: string })?.code;
      if (balanceEmailFlowChosenRef.current || errorCode === 'ERR_CANCELED') {
        return;
      }
      console.error('Error generating balance sheet:', error);
      const promptResult = await showReportRetryOrEmailPrompt(
        'No fue posible descargar el reporte en este momento. Puede reintentarlo, enviarlo por correo o cancelar.'
      );
      if (promptResult.isConfirmed) {
        handleGenerateBalanceSheet();
      } else if (promptResult.isDenied) {
        handleSendBalanceSheetByEmail();
      }
    } finally {
      setIsGeneratingBalance(false);
    }
  };


  return (
    <div>
      {/* ---------- PAGE HEAD ---------- */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {/*
            Antes el h1 ("Administrador de reportes") iba ANTES del
            breadcrumb, con un eyebrow arriba y el breadcrumb diciendo otra
            cosa ("Reportes") — dos titulos con texto distinto en la misma
            cabecera. Ahora hay uno solo, sin eyebrow: el ultimo segmento del
            breadcrumb ES el h1.
          */}
          <nav className="flex items-baseline gap-2" aria-label="Breadcrumb">
            <a
              href={homeRoute}
              data-testid="reports-breadcrumb-home-link"
              className="text-[14px] text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white"
            >
              Inicio
            </a>
            <span className="text-[14px] text-secondary-300">/</span>
            <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
              Reportes
            </h1>
          </nav>
          <p className="mt-2 text-[15px] text-secondary-500 dark:text-secondary-400">
            Genera reportes fiscales, contables y operativos según el período que necesites.
          </p>
        </div>

        {/*
          Resumen de formatos. Es un recuento de lo que ya esta en esta pantalla
          (3 tipos de reporte, dos formatos de salida), no un dato nuevo.
        */}
        <div className="rounded-[16px] border border-[#ececec] bg-white px-6 py-[18px] shadow-[0_1px_2px_rgba(10,34,51,.03),0_10px_24px_-14px_rgba(10,34,51,.14)] dark:border-secondary-700 dark:bg-secondary-800">
          <div className={METRIC_LABEL}>Formatos disponibles</div>
          <div className="mt-3 flex items-center gap-2.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d9488]/10 px-3 py-1.5 font-mono text-[12px] font-bold tracking-[0.14em] text-[#0d9488]">
              <TableCellsIcon className="h-3 w-3" />
              XLSX
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#6b239e]/10 px-3 py-1.5 font-mono text-[12px] font-bold tracking-[0.14em] text-[#6b239e]">
              <DocumentTextIcon className="h-3 w-3" />
              PDF
            </span>
          </div>
          <div className="mt-3 text-xs text-secondary-500 dark:text-secondary-400">
            <b className="text-ink-850 dark:text-white">3 tipos</b> de reporte configurables
          </div>
        </div>
      </div>

      {/* ---------- SECTION TITLE ---------- */}
      <div className="mb-4 mt-8 flex items-baseline gap-4">
        <div className="flex items-baseline gap-3">
          <span className={SECTION_NUM}>01 · CATÁLOGO</span>
          <h2 className="text-xl font-extrabold tracking-[-0.02em] text-ink-850 dark:text-white">
            Generador de reportes
          </h2>
        </div>
        <span className="h-px flex-1" style={DASHED_RULE_STYLE} aria-hidden="true" />
      </div>

      {/* ---------- CATÁLOGO ---------- */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
        {/* 1 · Documentos emitidos */}
        <ReportCard
          index="01"
          format="xlsx"
          icon={<DocumentTextIcon />}
          title="Documentos emitidos"
          description="Excel con las facturas emitidas correctamente en una fecha."
          footnote={
            <div className="flex items-center gap-2 border-t border-dashed border-[#eef0f2] pt-3.5 text-[12px] text-secondary-400 dark:border-secondary-700">
              <ClockIcon className="h-3 w-3" />
              Generación instantánea
            </div>
          }
          action={
            <button
              type="button"
              className={cn(CTA, 'w-full justify-center py-3 text-[14px] font-bold')}
              onClick={handleGenerateIssuedDocuments}
              disabled={!invoiceDate || isGeneratingExcel}
              data-testid="reports-issued-documents-generate-button"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              {isGeneratingExcel ? 'Generando...' : 'Generar reporte'}
            </button>
          }
        >
          <DateLabel>Fecha</DateLabel>
          <Input
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            data-testid="reports-issued-documents-date-input"
            className="font-mono font-semibold"
            required
          />
        </ReportCard>

        {/* 2 · Reporte Z */}
        <ReportCard
          index="02"
          format="pdf"
          featured
          icon={<ChartBarIcon />}
          title="Reporte Z"
          description="PDF con el total de las facturas emitidas durante un rango de fechas."
          footnote={
            <BranchFilter
              branchOffices={branchOffices}
              isLoading={isLoadingBranches}
              testIdBase="reports-z-branch-checkbox"
              selected={zStoreCodes}
              onToggle={(code, checked) =>
                setZStoreCodes(
                  checked ? [...zStoreCodes, code] : zStoreCodes.filter((c) => c !== code)
                )
              }
            />
          }
          action={
            <button
              type="button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-gradient-to-br from-[#6b239e] to-[#4a1670] px-[18px] py-3 text-[14px] font-bold text-white shadow-[0_8px_20px_-8px_rgba(107,35,158,.5)] transition-shadow hover:shadow-[0_12px_26px_-8px_rgba(107,35,158,.65)] disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleGenerateZReport}
              disabled={!zStartDate || !zEndDate || isGeneratingZ}
              data-testid="reports-z-generate-button"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              {isGeneratingZ ? 'Generando...' : 'Generar reporte'}
            </button>
          }
        >
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <DateLabel>Fecha inicio</DateLabel>
              <Input
                type="date"
                value={zStartDate}
                onChange={(e) => setZStartDate(e.target.value)}
                data-testid="reports-z-start-date-input"
                className="font-mono font-semibold"
                required
              />
            </div>
            <div>
              <DateLabel>Fecha fin</DateLabel>
              <Input
                type="date"
                value={zEndDate}
                onChange={(e) => setZEndDate(e.target.value)}
                data-testid="reports-z-end-date-input"
                className="font-mono font-semibold"
                required
              />
            </div>
          </div>
        </ReportCard>

        {/* 3 · Balance general */}
        <ReportCard
          index="03"
          format="xlsx"
          icon={<TableCellsIcon />}
          title="Balance general"
          description="Excel con todas las facturas registradas en el sistema según un rango de tiempo."
          footnote={
            <BranchFilter
              branchOffices={branchOffices}
              isLoading={isLoadingBranches}
              testIdBase="reports-balance-branch-checkbox"
              selected={balanceStoreCodes}
              onToggle={(code, checked) =>
                setBalanceStoreCodes(
                  checked
                    ? [...balanceStoreCodes, code]
                    : balanceStoreCodes.filter((c) => c !== code)
                )
              }
            />
          }
          action={
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className={cn(CTA, 'w-full justify-center py-3 text-[14px] font-bold')}
                onClick={handleGenerateBalanceSheet}
                disabled={!balanceInitDate || !balanceEndDate || isGeneratingBalance || isSendingBalanceByEmail}
                data-testid="reports-balance-generate-button"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                {isGeneratingBalance ? 'Generando...' : 'Generar reporte'}
              </button>
              <button
                type="button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] border border-teal-500/30 bg-teal-500/10 px-[18px] py-2.5 text-[13px] font-bold text-[#0d9488] transition-colors hover:bg-teal-500/15 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleSendBalanceSheetByEmail}
                disabled={!balanceInitDate || !balanceEndDate || isGeneratingBalance || isSendingBalanceByEmail}
                data-testid="reports-balance-email-button"
              >
                <EnvelopeIcon className="h-3.5 w-3.5" />
                {isSendingBalanceByEmail ? 'Enviando...' : 'Generar y enviar por correo'}
              </button>
            </div>
          }
        >
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <DateLabel>Fecha inicio</DateLabel>
              <Input
                type="date"
                value={balanceInitDate}
                onChange={(e) => setBalanceInitDate(e.target.value)}
                data-testid="reports-balance-start-date-input"
                className="font-mono font-semibold"
                required
              />
            </div>
            <div>
              <DateLabel>Fecha fin</DateLabel>
              <Input
                type="date"
                value={balanceEndDate}
                onChange={(e) => setBalanceEndDate(e.target.value)}
                data-testid="reports-balance-end-date-input"
                className="font-mono font-semibold"
                required
              />
            </div>
          </div>
        </ReportCard>
      </div>
    </div>
  );
}
